import { useEffect, useRef, useState } from "react";
import { markerUrl, MARKER_GROUPS } from "@/data/markers.ts";
import { ORDER_VERBS, squadColor, type OrderKind } from "@/data/squads.ts";
import { compassPoint, type GameCoord } from "@/engine/coords.ts";
import { gridLabel, type GridConfig } from "@/engine/grid.ts";
import { getWeaponProfile, solveFiring, WEAPON_PROFILES } from "@/engine/range.ts";
import { currentOrderFor } from "@/session/sessionDoc.ts";
import type { Marker, MarkerKind } from "@/session/schemas.ts";
import type { SessionApi } from "@/session/useSession.ts";

interface MapContextMenuProps {
  readonly session: SessionApi;
  readonly grid: GridConfig;
  /** Where on screen to anchor the menu — the point that was right-clicked. */
  readonly screenX: number;
  readonly screenY: number;
  readonly at: GameCoord;
  readonly place: string;
  /** Start drawing a flight-path arrow from this point. */
  readonly onStartRoute: (at: GameCoord) => void;
  readonly onClose: () => void;
}

/**
 * Right-click, and the map tells you what it can do right here.
 *
 * The bottom console makes you decide the squad and the verb before you ever
 * touch the map — fine once you know it, backwards the first time, since the
 * one thing you are always sure of first is *where*. This starts from where:
 * right-click a spot, and ordering a squad there, dropping a marker there, or
 * checking whether your own guns can even reach it are all right under the
 * cursor, in at most two taps.
 */
export function MapContextMenu({
  session,
  grid,
  screenX,
  screenY,
  at,
  place,
  onStartRoute,
  onClose,
}: MapContextMenuProps): React.ReactElement {
  const [squadId, setSquadId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    const onPointerDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) onClose();
    };
    // A capturing listener, so a second right-click elsewhere on the map
    // closes this one instead of both fighting over the same menu.
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("contextmenu", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("contextmenu", onPointerDown, true);
    };
  }, [onClose]);

  const artilleryMarkers = session.markers.filter((marker) => marker.weaponId);

  const issueOrAmend = (kind: OrderKind): void => {
    if (!squadId) return;
    const existing = currentOrderFor(session.orders, squadId);
    if (existing) {
      session.amend(existing.id, { kind, place, at });
    } else {
      session.issue({ squadId, kind, place, at });
    }
    onClose();
  };

  const placeMarker = (kind: MarkerKind, weaponId?: string, threat?: boolean): void => {
    session.putMarker({
      kind,
      at,
      ...(weaponId ? { weaponId } : {}),
      ...(threat ? { threat: true } : {}),
    });
    onClose();
  };

  // Position the menu so it never clips the viewport. If there is more room
  // above the click than below, the menu grows upward instead of downward —
  // which is what happens when you right-click near the bottom of the map,
  // the most common case for an artillery crew checking range on a target
  // south of their gun.
  const width = 264;
  const winH = typeof window === "undefined" ? 900 : window.innerHeight;
  const winW = typeof window === "undefined" ? 1400 : window.innerWidth;
  const spaceBelow = winH - screenY - 12;
  const spaceAbove = screenY - 12;
  const growUp = spaceBelow < 300 && spaceAbove > spaceBelow;
  const left = Math.min(screenX, winW - width - 12);
  const topStyle = growUp ? undefined : screenY;
  const bottomStyle = growUp ? winH - screenY : undefined;

  return (
    <div
      ref={rootRef}
      className="fixed z-30 flex flex-col rounded border border-border-strong bg-surface-raised shadow-lg"
      style={{
        left,
        width,
        ...(topStyle !== undefined ? { top: topStyle } : {}),
        ...(bottomStyle !== undefined ? { bottom: bottomStyle } : {}),
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="flex items-center justify-between border-b border-border-subtle px-2 py-1.5">
        <span className="numeric text-xs font-semibold text-accent">{place}</span>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          className="rounded px-1 text-xs text-text-muted hover:text-text-primary"
        >
          ×
        </button>
      </div>

      <div>
        {artilleryMarkers.length > 0 && (
          <FireSupportSection markers={artilleryMarkers} target={at} grid={grid} />
        )}

        {session.isCommander && (
          <div className="border-b border-border-subtle px-2 py-1.5">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[10px] font-medium tracking-wide text-text-muted uppercase">
                Order here
              </span>
              {squadId && (
                <button
                  type="button"
                  onClick={() => setSquadId(null)}
                  className="text-[10px] text-text-muted hover:text-text-primary"
                >
                  ← squad
                </button>
              )}
            </div>

            {session.squads.length === 0 ? (
              <p className="text-[11px] text-text-muted">No squads yet.</p>
            ) : !squadId ? (
              <div className="flex flex-wrap gap-1">
                {session.squads.map((squad) => {
                  const color = squadColor(squad.id);
                  const tag = session.squadTags.get(squad.id) ?? "?";
                  const tasked = currentOrderFor(session.orders, squad.id);
                  return (
                    <button
                      key={squad.id}
                      type="button"
                      onClick={() => setSquadId(squad.id)}
                      title={
                        tasked
                          ? `${squad.name || squad.id} — already tasked`
                          : squad.name || squad.id
                      }
                      className="numeric relative flex h-7 w-7 items-center justify-center rounded text-[11px] font-bold"
                      style={{ backgroundColor: color, color: "oklch(0.16 0.01 250)" }}
                    >
                      {tag}
                      {tasked && (
                        <span
                          aria-hidden
                          className="absolute right-0.5 bottom-0.5 h-1 w-1 rounded-full"
                          style={{ backgroundColor: "oklch(0.16 0.01 250)" }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-1">
                  {ORDER_VERBS.map((verb) => (
                    <button
                      key={verb.id}
                      type="button"
                      onClick={() => issueOrAmend(verb.id)}
                      title={verb.blurb}
                      className="rounded border border-border-subtle px-2 py-1 text-[11px] text-text-secondary transition-colors hover:border-accent hover:text-accent"
                    >
                      {verb.label}
                    </button>
                  ))}
                </div>
                {(() => {
                  const existing = currentOrderFor(session.orders, squadId);
                  if (!existing) return null;
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        session.cancel(existing.id);
                        onClose();
                      }}
                      className="mt-1.5 w-full rounded border border-danger/40 px-2 py-1 text-left text-[11px] text-danger transition-colors hover:bg-danger/10"
                    >
                      Withdraw current order
                    </button>
                  );
                })()}
              </>
            )}
          </div>
        )}

        <div className="border-b border-border-subtle px-2 py-1.5">
          <button
            type="button"
            onClick={() => {
              onStartRoute(at);
              onClose();
            }}
            className="w-full rounded border border-border-subtle px-2 py-1 text-left text-[11px] text-text-secondary transition-colors hover:border-accent hover:text-accent"
          >
            Draw flight path from here
          </button>
        </div>

        <MarkerSection placeMarker={placeMarker} />
      </div>
    </div>
  );
}

/**
 * Compact icon rows with an instant hover label — two clicks total (right-
 * click to open, click icon to place), not three. The label floats next to
 * whatever icon is under the cursor so you never have to guess, but doesn't
 * take up a full row per item the way a written-out list does.
 */
/**
 * Compact icon grid with a floating label that follows the cursor — the
 * name appears right next to whatever icon you are pointing at, instantly,
 * without the browser's tooltip delay and without leaving the spot your
 * eyes are already looking at.
 */
/**
 * Marker palette with a cursor-following label that never affects layout.
 *
 * The label is `position: fixed` so it floats over everything without
 * pushing the menu taller or triggering a scrollbar — the previous
 * `position: absolute` version sat inside the menu's flow and grew it.
 */
function MarkerSection({
  placeMarker,
}: {
  readonly placeMarker: (kind: MarkerKind, weaponId?: string, threat?: boolean) => void;
}): React.ReactElement {
  const [tooltip, setTooltip] = useState<{
    label: string;
    x: number;
    y: number;
  } | null>(null);

  const show = (label: string, event: React.MouseEvent): void => {
    setTooltip({ label, x: event.clientX + 14, y: event.clientY - 8 });
  };
  const move = (event: React.MouseEvent): void => {
    setTooltip((prev) =>
      prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 8 } : null,
    );
  };
  const hide = (): void => setTooltip(null);

  return (
    <div className="px-2 py-1.5">
      <div className="mb-1 text-[10px] font-medium tracking-wide text-text-muted uppercase">
        Place marker
      </div>
      <div className="space-y-1">
        {MARKER_GROUPS.map((group) => (
          <div key={group.id}>
            <div className="mb-0.5 text-[9px] text-text-muted">{group.label}</div>
            <div className="flex flex-wrap gap-1">
              {group.choices.map((choice) =>
                choice.icon === "artillery" ? (
                  WEAPON_PROFILES.map((weapon) => {
                    const label = choice.threat
                      ? `${choice.label} (${weapon.label})`
                      : weapon.label;
                    return (
                      <button
                        key={`${choice.threat ? "t-" : ""}${weapon.id}`}
                        type="button"
                        onMouseEnter={(e) => show(label, e)}
                        onMouseMove={move}
                        onMouseLeave={hide}
                        onClick={() => placeMarker(choice.icon, weapon.id, choice.threat)}
                        className={`flex h-7 w-7 items-center justify-center rounded border transition-colors hover:border-accent ${choice.threat ? "border-danger/40" : "border-border-subtle"}`}
                      >
                        <img
                          src={markerUrl(choice.icon)}
                          alt={label}
                          width={18}
                          height={18}
                          className={choice.threat ? "hue-rotate-180" : undefined}
                        />
                      </button>
                    );
                  })
                ) : (
                  <button
                    key={`${choice.threat ? "t-" : ""}${choice.icon}`}
                    type="button"
                    onMouseEnter={(e) => show(choice.label, e)}
                    onMouseMove={move}
                    onMouseLeave={hide}
                    onClick={() => placeMarker(choice.icon, undefined, choice.threat)}
                    className={`flex h-7 w-7 items-center justify-center rounded border transition-colors hover:border-accent ${choice.threat ? "border-danger/40" : "border-border-subtle"}`}
                  >
                    <img
                      src={markerUrl(choice.icon)}
                      alt={choice.label}
                      width={18}
                      height={18}
                      className={choice.threat ? "hue-rotate-180" : undefined}
                    />
                  </button>
                ),
              )}
            </div>
          </div>
        ))}
      </div>

      {tooltip && (
        <div
          className="pointer-events-none fixed z-50 whitespace-nowrap rounded bg-surface-sunken px-1.5 py-0.5 text-[11px] text-text-primary shadow"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.label}
        </div>
      )}
    </div>
  );
}

/**
 * Fire support — compact by default, click to expand.
 *
 * Each gun is one line: name, distance, bearing, in/out of range. That is
 * enough to decide whether to fire; the full card (grid ref, degrees,
 * compass, min/max range) appears only when you click that line, since most
 * of the time you just want to know "can this gun hit there, and roughly
 * which way."
 */
function FireSupportSection({
  markers,
  target,
  grid,
}: {
  readonly markers: readonly Marker[];
  readonly target: GameCoord;
  readonly grid: GridConfig;
}): React.ReactElement {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="border-b border-border-subtle px-2 py-1.5">
      <div className="mb-1 text-[10px] font-medium tracking-wide text-text-muted uppercase">
        Fire support
      </div>
      <ul className="space-y-0.5">
        {markers.map((marker) => {
          const weapon = marker.weaponId ? getWeaponProfile(marker.weaponId) : undefined;
          if (!weapon) return null;
          const solution = solveFiring(marker.at, target, weapon);
          const inRange = solution.status === "inRange";
          const isThreat = Boolean(marker.threat);
          const expanded = expandedId === marker.id;

          return (
            <li key={marker.id}>
              <button
                type="button"
                onClick={() => setExpandedId(expanded ? null : marker.id)}
                className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left transition-colors hover:bg-surface-sunken"
              >
                <span
                  className={`numeric text-[11px] font-semibold ${isThreat ? "text-danger" : "text-text-primary"}`}
                >
                  {isThreat ? "▲ " : ""}
                  {weapon.label}
                </span>
                <span className="numeric text-[10px] text-text-muted">
                  {Math.round(solution.distanceMeters)}m · {Math.round(solution.bearingMils)}{" "}
                  mil
                </span>
                <span
                  className={`numeric ml-auto text-[10px] font-semibold ${inRange ? "text-success" : "text-danger"}`}
                >
                  {inRange
                    ? "✓"
                    : solution.status === "tooClose"
                      ? `−${Math.round(solution.outByMeters)}m`
                      : `+${Math.round(solution.outByMeters)}m`}
                </span>
              </button>

              {expanded && (
                <div className="numeric mt-0.5 mb-1 ml-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 border-l-2 border-border-subtle pl-2 text-[10px]">
                  <span className="text-text-muted">Gun</span>
                  <span className="text-text-secondary">{gridLabel(marker.at, grid, 1)}</span>
                  <span className="text-text-muted">Bearing</span>
                  <span className="text-text-secondary">
                    {Math.round(solution.bearingDegrees)}° (
                    {compassPoint(solution.bearingDegrees)}) ·{" "}
                    {Math.round(solution.bearingMils)} mil
                  </span>
                  <span className="text-text-muted">Range</span>
                  <span className="text-text-secondary">
                    {weapon.minRangeMeters}–{weapon.maxRangeMeters}m
                  </span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
